// Premature-Handover Report — per target bot, counts sessions that
// escalated to a human WITHOUT ever invoking one of the bot's declared
// business-outcome tools. These are the "bot bailed before trying"
// sessions.
//
// WHY this report exists:
//   A bot that transfers to a live agent at the end of a completed
//   booking is doing its job. A bot that transfers to a human WITHOUT
//   ever having tried its primary workflow — never called
//   ScheduleAppointment, never called SendMyChartMessage — is bailing.
//   Supervisors have asked for a single number per bot that tells them
//   whether tuning the prompt would move the needle; this is that
//   number.
//
// SIGNALS used:
//   PRE-FILTER (cheap, from session row):
//     * resolved bot name is in the catalog's business_outcomes set
//     * interactionOutcome === "ESCALATED" OR lastAction === "Agent handover"
//
//   DEEP (per candidate session, from fetched messages):
//     * metadata.tools[].tool_name — compared against the bot's declared
//       business-outcome tool names
//     * metadata.tools[].state      — only SUCCESS invocations count;
//                                     a failed tool call is a legitimate
//                                     escalation reason, so it doesn't
//                                     disqualify a session from being
//                                     counted premature.
//
// GAPS:
//   * We don't distinguish "caller interrupted the bot before it could
//     call a tool" from "bot refused to call the tool". Both land in
//     premature. The avgDuration column helps triage: very short avgs
//     (<60s) often mean the caller forced the escalation; long avgs
//     (>3min) often mean the bot got stuck.
//   * If an outcome tool CALLED and FAILED, the session isn't counted
//     premature — the bot tried. That's intentional; the Epic Error
//     Detector surfaces those separately.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Progress,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconInfoCircle,
  IconPlayerStopFilled,
  IconSparkles,
} from '@tabler/icons-react';

import catalogData from '../data/botCatalog.json';
import { ReportTable } from './ReportTable';
import { SessionListModal } from '../components/SessionListModal';
import { callTool } from '../../../api_scripts/wxccMcpClient';
import { fmtDuration } from '../utils';
import {
  classifyPrematureHandovers,
  countCandidates,
} from '../lib/prematureHandover';

const REPORT_ID = 'premature-handover';
const RATE_GAP_MS = 2200;

const COLUMNS = [
  {
    id: 'bot',
    label: 'Bot',
    pinned: true,
    render: (r) => <Text size="sm" fw={500}>{r.bot.replace(/^PROD-/, '')}</Text>,
  },
  {
    id: 'totalHandovers',
    label: 'Total handovers',
    align: 'right',
    hint: 'Sessions on this bot that escalated OR ended on Agent handover.',
    render: (r) => <Text size="sm" fw={600}>{r.totalHandovers}</Text>,
  },
  {
    id: 'premature',
    label: 'Premature',
    align: 'right',
    hint: 'Handovers where NO business-outcome tool fired. The bot bailed before trying.',
    render: (r) => <Badge size="sm" color={r.premature > 0 ? 'orange' : 'gray'} variant={r.premature > 0 ? 'filled' : 'light'}>{r.premature}</Badge>,
  },
  {
    id: 'rate',
    label: 'Rate',
    align: 'right',
    hint: 'Premature ÷ total handovers.',
    render: (r) => {
      const color = r.rate >= 50 ? 'red' : r.rate >= 25 ? 'orange' : r.rate > 0 ? 'yellow' : 'teal';
      return <Badge size="sm" color={color} variant="light">{r.rate}%</Badge>;
    },
  },
  {
    id: 'avgDuration',
    label: 'Avg duration',
    align: 'right',
    hint: 'Average session duration across the handovers on this bot. <60s avg usually means callers forced the escalation; >3min means the bot got stuck.',
    render: (r) => <Text size="sm">{r.avgDurationMs != null ? fmtDuration(r.avgDurationMs) : '—'}</Text>,
  },
];

const DEFAULT_VISIBLE = ['totalHandovers', 'premature', 'rate', 'avgDuration'];

export function PrematureHandover({ data, onViewSession, window }) {
  const sessions = useMemo(() => data?.sessions ?? [], [data?.sessions]);
  const [agentIdByName, setAgentIdByName] = useState({});
  const [agentIdToName, setAgentIdToName] = useState({});
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const [drill, setDrill] = useState(null); // { bot, sessions }

  useEffect(() => {
    let cancelled = false;
    callTool('ops', 'wxcc-operations-list-ai-agents', { view: 'list' })
      .then((res) => {
        if (cancelled) return;
        const forward = {};
        const reverse = {};
        for (const a of res?.agents ?? []) {
          if (a.name && a.id) {
            forward[a.name] = a.id;
            reverse[a.id] = a.name;
          }
        }
        setAgentIdByName(forward);
        setAgentIdToName(reverse);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const candidateCount = useMemo(
    () => countCandidates(sessions, catalogData, agentIdToName),
    [sessions, agentIdToName],
  );
  const estimatedSeconds = Math.ceil((candidateCount * RATE_GAP_MS) / 1000);

  async function runScan() {
    if (running) return;
    setRunning(true);
    setError(null);
    setResult(null);
    setProgress({ done: 0, total: candidateCount });
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const r = await classifyPrematureHandovers(sessions, catalogData, {
        agentIdByName,
        agentIdToName,
        signal: controller.signal,
        onProgress: (done, total) => setProgress({ done, total }),
        fetchMessages: async (agentId, sessionId) => {
          await new Promise((res) => setTimeout(res, RATE_GAP_MS));
          if (controller.signal.aborted) throw new Error('aborted');
          return callTool('ops', 'wxcc-operations-get-ai-agent-session-messages', {
            agentId, sessionId, limit: 100,
          });
        },
      });
      setResult(r);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setRunning(false);
      abortRef.current = null;
    }
  }

  function cancelScan() {
    abortRef.current?.abort();
    setRunning(false);
  }

  const rows = useMemo(() => {
    if (!result) return [];
    return Object.entries(result.perBot)
      .map(([bot, r]) => ({ key: bot, bot, ...r }))
      .sort((a, b) => b.premature - a.premature);
  }, [result]);

  const csvRows = rows.map((r) => ({
    bot: r.bot,
    total_handovers: r.totalHandovers,
    premature: r.premature,
    premature_rate_pct: r.rate,
    avg_duration_ms: r.avgDurationMs != null ? Math.round(r.avgDurationMs) : '',
  }));

  const onRowClick = (r) => {
    const idSet = r.prematureSessionIds instanceof Set
      ? r.prematureSessionIds
      : new Set(r.prematureSessionIds ?? []);
    setDrill({
      bot: r.bot,
      sessions: sessions.filter((s) => idSet.has(s.sessionId)),
    });
  };

  return (
    <Stack gap="xs">
      <Group justify="space-between" align="flex-end">
        <div>
          <Title order={4}>Premature-handover report</Title>
          <Text size="xs" c="dimmed">
            Per target bot: sessions that escalated to a human WITHOUT the bot ever successfully
            invoking one of its declared business-outcome tools. Deep-scans per-session messages
            — pre-filtered to escalated sessions on target bots so the fetch stays cheap.
          </Text>
        </div>
        <Group gap="xs">
          {!running && (
            <Button
              size="xs"
              variant="light"
              color="violet"
              leftSection={<IconSparkles size={14} />}
              onClick={runScan}
              disabled={candidateCount === 0 || Object.keys(agentIdByName).length === 0}
            >
              {result ? 'Re-run deep scan' : 'Deep scan'}
            </Button>
          )}
          {running && (
            <>
              <Text size="xs" c="dimmed">
                Scanning {progress.done}/{progress.total}
              </Text>
              <Button
                size="xs"
                variant="light"
                color="red"
                leftSection={<IconPlayerStopFilled size={14} />}
                onClick={cancelScan}
              >
                Cancel
              </Button>
            </>
          )}
          {result && !running && (
            <Badge color="violet" variant="light">Scanned {result.totalScanned} session{result.totalScanned === 1 ? '' : 's'}</Badge>
          )}
        </Group>
      </Group>

      {!result && !running && (
        <Alert color="blue" variant="light" icon={<IconInfoCircle size={14} />}>
          <Text size="xs">
            {candidateCount} candidate handover session{candidateCount === 1 ? '' : 's'} in window
            across bots with declared business outcomes. Scan takes ~{estimatedSeconds}s at the Cisco
            MCP 2.2s/req cap.
          </Text>
        </Alert>
      )}

      {running && progress.total > 0 && (
        <Progress
          value={(progress.done / progress.total) * 100}
          size="sm"
          color="violet"
          striped
          animated
        />
      )}

      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={14} />}>
          Deep scan failed: {error}
        </Alert>
      )}

      {result && (
        <ReportTable
          title={`Premature-handover rollup — ${rows.length} target bot${rows.length === 1 ? '' : 's'}`}
          subtitle="Rate ≥50% is red (the bot is mostly bailing), 25-50% orange (needs tuning), <25% yellow, 0% teal. Click a row to see the premature sessions."
          reportId={REPORT_ID}
          columns={COLUMNS}
          defaultVisibleIds={DEFAULT_VISIBLE}
          rows={rows}
          emptyLabel="No handover sessions on target bots in this window."
          onRowClick={onViewSession ? onRowClick : undefined}
          csvExport={{
            filename: `premature-handover-${new Date().toISOString().slice(0, 10)}.csv`,
            rows: csvRows,
          }}
        />
      )}

      <SessionListModal
        opened={!!drill}
        onClose={() => setDrill(null)}
        title={drill ? `${drill.bot.replace(/^PROD-/, '')} — premature handovers` : ''}
        subtitle={drill ? `${drill.sessions.length} session${drill.sessions.length === 1 ? '' : 's'} escalated without any business tool firing` : ''}
        sessions={drill?.sessions ?? []}
        onViewSession={onViewSession}
        window={window}
      />
    </Stack>
  );
}
